import type {
  AreaEntity,
  ConsolidatedCenterLayerData,
  EnrichedCareerEntity,
} from '@/domains/centerLayer/centerLayer.entity';
import type { DayName } from '@/domains/shared';

/**
 * Filtros para extracción de áreas
 */
interface ExtractAreasFilters {
  jornada: DayName | null;
}

/**
 * Use Case para extraer áreas construidas dinámicamente desde carreras
 * Cumple SRP: Solo se encarga de extraer carreras y construir áreas con conteo real
 * Cumple OCP: Abierto a extensión (nuevos filtros) cerrado a modificación
 *
 * Responsabilidades:
 * - Filtrar carreras por jornada (si aplica)
 * - Consolidar carreras de múltiples jornadas
 * - Construir áreas dinámicamente contando carreras únicas por área
 * - Cada carrera cuenta individualmente (misma carrera en diurno/vespertino = 2 carreras)
 */
export class ExtractAreasUseCase {
  /**
   * Extrae carreras según filtro de jornada
   * @param apiData - Datos consolidados con estructura por jornada
   * @param jornada - Jornada específica o null para consolidar ambas
   * @returns Array de carreras enriquecidas
   */
  private extractCareers(
    apiData: ConsolidatedCenterLayerData,
    jornada: DayName | null,
  ): EnrichedCareerEntity[] {
    if (jornada) {
      return apiData[jornada]?.carreras ?? [];
    }

    const diurnoCareers = apiData.diurno?.carreras ?? [];
    const vespertinoCareers = apiData.vespertino?.carreras ?? [];

    return [...diurnoCareers, ...vespertinoCareers];
  }

  /**
   * Construye áreas dinámicamente desde carreras
   * Agrupa por nombre de área y cuenta carreras únicas
   * @param careers - Array de carreras enriquecidas
   * @returns Array de áreas con conteo de carreras
   */
  private buildAreasFromCareers(careers: EnrichedCareerEntity[]): AreaEntity[] {
    if (!careers || careers.length === 0) return [];

    const areaMap = new Map<string, number>();

    for (const career of careers) {
      const currentCount = areaMap.get(career.area) || 0;
      areaMap.set(career.area, currentCount + 1);
    }

    return Array.from(areaMap.entries()).map(([nombre, totalCarrera]) => ({
      nombre,
      'total-carrera': totalCarrera,
    }));
  }

  /**
   * Ejecuta la extracción de carreras y construcción de áreas
   * @param apiData - Datos consolidados del centro
   * @param filters - Filtros de jornada
   * @returns Áreas construidas con conteo real de carreras
   */
  execute(
    apiData: ConsolidatedCenterLayerData | null,
    filters: ExtractAreasFilters,
  ): AreaEntity[] {
    if (!apiData) return [];

    const careers = this.extractCareers(apiData, filters.jornada);

    return this.buildAreasFromCareers(careers);
  }
}
